import React, { useCallback, useMemo, useRef, useState } from "react";
import { toastManager, useIsMobile } from "@salestrak/ui";
import { useFragment } from "@apollo/client/react";
import type { DetailViewSize } from "#app/components/detail-view/detail-view-popup";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { IssueViewWorkflowRefFragmentDoc, type IssueRow } from "#app/lib/data";
import type { IssueViewTab } from "./issue-view-types";
import { IssueViewContext, OpenIssueIdContext } from "./use-issue-view";
import type { OpenIssueOptions, OpenCreateIssueOptions } from "./use-issue-view";
import { useFetchIssueById } from "#app/lib/hooks/use-fetch-issue-by-id";
import { useRegisterSubscriptions } from "#app/lib/subscriptions/subscription-provider";
import { issueViewSubscriptions } from "#app/hooks/issue-view/issue-view-subscriptions";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { IssueViewDialog } from "./issue-view-dialog";
import { IssueViewSheet } from "./issue-view-sheet";
import {
  IssueViewDialogStateContext,
  useIssueViewDialogState,
  type IssueViewDialogState,
  type ModalState,
} from "./issue-view-dialog-state";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useRecentItems } from "#app/lib/hooks/use-recent-items";
import { usePathnameChange } from "#app/lib/hooks/use-pathname-change";
import { useAnalytics, AnalyticsContext, IssueEvent } from "#app/lib/analytics";
import {
  useDetailStackRaiseNonce,
  useDetailStackSlot,
} from "#app/components/detail-view/detail-stack";

const DESKTOP_SIZE_STORAGE_KEY = "issue-view-dialog-desktop-size";

// Routes that should keep the SIDE-POSITION issue view open across
// navigation: the map and any workflow screen (board, list, or the bare
// `/workflow/:id` redirect step) across any workflow. All other route changes
// close it — and the fullscreen presentation closes on every route change,
// because only the side panel is meant to ride along while the user moves
// around the app.
const KEEP_OPEN_PATTERNS = [/^\/[^/]+\/map$/, /^\/[^/]+\/workflow\/[^/]+(\/.*)?$/];

export function IssueViewProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ModalState | null>(null);
  const [activeTab, setActiveTab] = useState<IssueViewTab>("summary");
  const { state: persistedSize, update: updatePersistedSize } = usePersistedState<{
    desktopSize: DetailViewSize;
  }>(DESKTOP_SIZE_STORAGE_KEY, { desktopSize: "sidebar" });
  const desktopSize = persistedSize.desktopSize;
  const setDesktopSize = useCallback(
    (size: DetailViewSize) => updatePersistedSize({ desktopSize: size }),
    [updatePersistedSize],
  );
  // Read the live mode from a ref so openIssue stays referentially stable —
  // toggling sidebar/fullscreen shouldn't re-render every useIssueView consumer.
  const desktopSizeRef = useRef(desktopSize);
  desktopSizeRef.current = desktopSize;
  const onCreateCallbackRef = useRef<((issue: IssueRow) => void) | undefined>(undefined);
  const fetchIssueById = useFetchIssueById();
  const { replace } = useAppNavigate();
  const { activeWorkspace } = useWorkspace();
  const { track } = useAnalytics();
  const { recordVisit } = useRecentItems();

  const openIssue = useCallback(
    (issue: IssueRow, options?: OpenIssueOptions) => {
      // Record the open (as a display snapshot) so global search can render
      // its "Results" recents and float recent matches to the top, and the
      // My Work Recent widget can list it. The TYPE's icon is a project's
      // display identity — the per-issue icon column is a legacy default the
      // client offers no picker for. A Project in the trash is not
      // recorded: Recent lists places to go back to and work in.
      if (!issue.trashedAt) recordVisit({
        kind: "issue",
        id: issue.issueId,
        name: issue.name,
        iconName: issue.issueType?.icon?.icon ?? issue.icon?.icon ?? null,
        // Types are non-themed: their glyph carries no color, so only the
        // project's own icon (the fallback) brings one.
        iconColor: issue.issueType?.icon?.icon ? null : (issue.icon?.color ?? null),
        workflowId: issue.workflowId ?? null,
      });
      setActiveTab(options?.initialTab ?? "summary");
      setState({
        mode: "view",
        issue,
        initialTab: options?.initialTab ?? "summary",
        focusNoteId: options?.focusNoteId ?? null,
        focusUnread: options?.focusUnread ?? false,
        focusCustomFieldId: options?.focusCustomFieldId ?? null,
        // A fresh nonce per request, so re-opening the already-open issue
        // with focusTitle re-fires the summary's focus effect.
        focusTitleNonce: options?.focusTitle ? Date.now() : undefined,
        presentation: options?.presentation,
      });
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.View,
        data: {
          // The presentation that shows, not only the stored desktop size: a
          // contextual open can override it.
          mode: options?.presentation ?? desktopSizeRef.current,
          source: options?.source ?? "unknown",
          initialTab: options?.initialTab ?? "summary",
        },
      });
    },
    [track, recordVisit],
  );

  const openIssueById = useCallback(
    (issueId: string, options?: OpenIssueOptions) => {
      fetchIssueById(issueId)
        .then((issue) => {
          if (issue) {
            openIssue(issue, options);
            return;
          }
          // Query SUCCEEDED but the issue isn't visible to this viewer
          // (private + no share, or deleted). Don't leave them on an empty
          // workflow screen — tell them why and send them home. Only navigate
          // when a workspace is actually loaded; otherwise the home href would
          // be malformed (empty workspace segment) — the toast still informs.
          toastManager.add({ title: "No access to this item", type: "error" });
          if (activeWorkspace?.urlPath) replace("/");
        })
        .catch((err) => {
          // A real fetch failure (network/server) — NOT an access problem.
          // Don't claim "no access" or yank the user out of their view.
          toastMutationError(err, "Failed to open item");
        });
    },
    [fetchIssueById, openIssue, replace, activeWorkspace?.urlPath],
  );

  const openCreateIssue = useCallback(
    (options: OpenCreateIssueOptions) => {
      onCreateCallbackRef.current = options.onCreated;
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.CreateStart,
        data: { source: options.source ?? "unknown" },
      });
      setState({
        mode: "create",
        workflowId: options.workflowId,
        stageId: options.stageId,
        address: options.address,
        name: options.name,
        issueSourceId: options.issueSourceId,
        teamId: options.teamId,
        source: options.source,
      });
    },
    [track],
  );

  const closeIssue = useCallback(() => setState(null), []);
  const closeIssueIfOpen = useCallback(
    (issueId: string) =>
      setState((current) =>
        current?.mode === "view" && current.issue.issueId === issueId ? null : current,
      ),
    [],
  );

  // The detail stack sees the VIEW presentation only. Its dismiss (a
  // top-level open of another kind) leaves a create draft alone — typed input
  // is never discarded by a click elsewhere.
  const dismissView = useCallback(
    () => setState((current) => (current?.mode === "view" ? null : current)),
    [],
  );
  useDetailStackSlot("issue", state?.mode === "view", dismissView);

  // While an issue is open, keep it live: another session's updateIssue
  // publishes issueUpdated/issueMoved on the issue's workflow, and the
  // handlers write the summary fields + custom-field values into the cache.
  // On surfaces with no issue surface mounted (map, deep links) this
  // registration is the only live wire for the open dialog.
  //
  // The workflow binding is read LIVE from the cache, not from the
  // openIssue-time snapshot: updateIssue can move the issue to another
  // workflow, and the server publishes on the issue's NEW workflow topic —
  // a snapshot binding would leave the dialog subscribed to a topic the
  // issue has left. The snapshot is only the pre-cache fallback.
  const openedIssue = state?.mode === "view" ? state.issue : null;
  const { data: liveIssueRef } = useFragment({
    fragment: IssueViewWorkflowRefFragmentDoc,
    from: { __typename: "Issue", issueId: openedIssue?.issueId ?? "" },
  });
  const openWorkflowId = openedIssue
    ? (liveIssueRef.workflowId ?? openedIssue.workflowId)
    : null;
  useRegisterSubscriptions(
    openWorkflowId ? issueViewSubscriptions(openWorkflowId) : [],
  );

  usePathnameChange((next) => {
    if (!state) return;
    if (
      desktopSizeRef.current === "sidebar" &&
      KEEP_OPEN_PATTERNS.some((r) => r.test(next))
    ) {
      return;
    }
    closeIssue();
  });

  const onCreated = useCallback(
    (issue: IssueRow) => {
      onCreateCallbackRef.current?.(issue);
      onCreateCallbackRef.current = undefined;
      openIssue(issue, { source: "create" });
    },
    [openIssue],
  );

  const contextValue = useMemo(
    () => ({ openIssue, openIssueById, openCreateIssue, closeIssue, closeIssueIfOpen }),
    [openIssue, openIssueById, openCreateIssue, closeIssue, closeIssueIfOpen],
  );

  const dialogState = useMemo<IssueViewDialogState>(
    () => ({ state, setState, activeTab, setActiveTab, desktopSize, setDesktopSize, onCreated, closeIssue }),
    [state, activeTab, desktopSize, setDesktopSize, onCreated, closeIssue],
  );

  return (
    <IssueViewContext.Provider value={contextValue}>
      <IssueViewDialogStateContext.Provider value={dialogState}>
        <OpenIssueIdContext.Provider value={openedIssue?.issueId ?? null}>
          {children}
        </OpenIssueIdContext.Provider>
      </IssueViewDialogStateContext.Provider>
    </IssueViewContext.Provider>
  );
}

export function IssueViewDialogs() {
  const isMobile = useIsMobile();
  const dialog = useIssueViewDialogState();
  // A raise remounts the dialog so its portal lands above the drawers that
  // opened after it — see DetailStackProvider.
  const raise = useDetailStackRaiseNonce("issue");
  const nested =
    dialog.state?.mode === "view" &&
    dialog.state.presentation === "fullscreen-dialog";
  // The owning dialog boundary renders contextual project dialogs inside its
  // own React subtree so Base UI can coordinate the nested modal stack.
  if (nested) return null;
  return isMobile ? (
    <IssueViewSheet key={raise} />
  ) : (
    <IssueViewDialog key={raise} />
  );
}
